import React, { useState, useRef, useCallback } from 'react';
import { Box, Typography, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloudUploadOutlined, FolderOpenOutlined, CloseOutlined,
  VideoLibraryOutlined, BrokenImageOutlined,
} from '@mui/icons-material';
import { api } from '../../../hooks/useApi';
import { ContentBrowserModal } from './ContentBrowserModal';

const PRIMARY = '#1075BA';

/**
 * MediaField
 *
 * A single unified drop zone that:
 *   • Shows a preview when a value is set (with a clear button)
 *   • Clicking anywhere opens the ContentBrowserModal
 *   • Dragging a file onto it uploads directly
 *   • A small upload icon button in the corner triggers a file input
 *
 * Props:
 *   value     — current URL string
 *   onChange  — (url: string) => void
 *   mediaType — 'image' | 'video'  (default: 'image')
 */
export function MediaField({ value, onChange, mediaType = 'image' }) {
  const [browserOpen, setBrowserOpen] = useState(false);
  const [dragging, setDragging]       = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [imgError, setImgError]       = useState(false);
  const inputRef   = useRef();
  const dragCount  = useRef(0);

  const hasValue = !!value;

  // ── Upload ───────────────────────────────────────────────────────────────

  const doUpload = useCallback(async (file) => {
    setUploadError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/media/upload', formData);
      onChange(data.data.url);
      setImgError(false);
    } catch (err) {
      setUploadError(err?.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  // ── Drag & drop ──────────────────────────────────────────────────────────
  // dragCount ref prevents flicker when cursor moves over child elements

  const onDragEnter = (e) => {
    e.preventDefault();
    dragCount.current += 1;
    if (dragCount.current === 1) setDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    dragCount.current -= 1;
    if (dragCount.current === 0) setDragging(false);
  };

  const onDragOver = (e) => e.preventDefault();

  const onDrop = useCallback((e) => {
    e.preventDefault();
    dragCount.current = 0;
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (mediaType === 'image' && !isImage) { setUploadError('Only image files are allowed here'); return; }
    if (mediaType === 'video' && !isVideo) { setUploadError('Only video files are allowed here'); return; }
    doUpload(file);
  }, [doUpload, mediaType]);

  // ── Render ───────────────────────────────────────────────────────────────

  const accept = mediaType === 'video'
    ? 'video/mp4,video/webm,video/ogg,video/quicktime'
    : 'image/jpeg,image/png,image/webp,image/gif,image/svg+xml';

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0]) doUpload(e.target.files[0]); e.target.value = ''; }}
      />

      {/* ── Unified drop zone ── */}
      <Box
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onClick={() => !uploading && setBrowserOpen(true)}
        sx={{
          position: 'relative',
          borderRadius: '8px',
          border: `1.5px dashed ${dragging ? PRIMARY : hasValue ? '#d4dbe6' : '#c8d4e0'}`,
          overflow: 'hidden',
          cursor: uploading ? 'default' : 'pointer',
          background: dragging ? alpha(PRIMARY, 0.04) : '#f8fafc',
          transition: 'all 0.13s',
          '&:hover': !uploading ? {
            borderColor: PRIMARY,
            background: alpha(PRIMARY, 0.03),
          } : {},
        }}
      >
        {/* ── Preview when value is set ── */}
        {hasValue && !uploading && (
          <>
            {mediaType === 'image' ? (
              !imgError ? (
                <Box
                  component="img"
                  src={value}
                  alt="preview"
                  onError={() => setImgError(true)}
                  sx={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <Box sx={{
                  height: 80, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 0.4,
                }}>
                  <BrokenImageOutlined sx={{ fontSize: 18, color: '#b0bac8' }} />
                  <Typography sx={{ fontSize: '0.6rem', color: '#b0bac8' }}>Preview unavailable</Typography>
                </Box>
              )
            ) : (
              <Box sx={{ height: 52, display: 'flex', alignItems: 'center', gap: 1, px: 1.25 }}>
                <VideoLibraryOutlined sx={{ fontSize: 16, color: PRIMARY, flexShrink: 0 }} />
                <Typography sx={{
                  fontSize: '0.67rem', color: '#3c4555', flex: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {value.split('/').pop()}
                </Typography>
              </Box>
            )}

            {/* Hover overlay with actions label */}
            <Box sx={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
              '&:hover': { background: 'rgba(0,0,0,0.32)' },
              '& .overlay-label': { opacity: 0, transition: 'opacity 0.15s' },
              '&:hover .overlay-label': { opacity: 1 },
            }}>
              <Box className="overlay-label" sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
              }}>
                <FolderOpenOutlined sx={{ fontSize: 14, color: '#fff' }} />
                <Typography sx={{ fontSize: '0.68rem', color: '#fff', fontWeight: 700 }}>
                  Browse library
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* ── Empty / uploading state ── */}
        {(!hasValue || uploading) && (
          <Box sx={{
            px: 1.25, py: 1.25,
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            {uploading ? (
              <>
                <CircularProgress size={13} sx={{ color: PRIMARY, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.69rem', color: PRIMARY, fontWeight: 600 }}>
                  Uploading…
                </Typography>
              </>
            ) : (
              <>
                <FolderOpenOutlined sx={{ fontSize: 14, color: '#9ea8b3', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.69rem', color: '#9ea8b3', flex: 1, lineHeight: 1.3 }}>
                  Browse library
                  <Box component="span" sx={{ color: '#b0bac8' }}> · or drop to upload</Box>
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* ── Corner action buttons (stop propagation so they don't open browser) ── */}
        <Box
          onClick={e => e.stopPropagation()}
          sx={{
            position: 'absolute', top: 4, right: 4,
            display: 'flex', gap: 0.4,
          }}
        >
          {/* Upload file button */}
          {!uploading && (
            <Tooltip title="Upload file" placement="top">
              <IconButton
                size="small"
                onClick={e => { e.stopPropagation(); inputRef.current?.click(); }}
                sx={{
                  width: 20, height: 20,
                  background: 'rgba(0,0,0,0.45)', color: '#fff',
                  '&:hover': { background: PRIMARY },
                }}
              >
                <CloudUploadOutlined sx={{ fontSize: 11 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Clear button — only when value is set */}
          {hasValue && !uploading && (
            <Tooltip title="Remove" placement="top">
              <IconButton
                size="small"
                onClick={e => { e.stopPropagation(); onChange(''); setImgError(false); }}
                sx={{
                  width: 20, height: 20,
                  background: 'rgba(0,0,0,0.45)', color: '#fff',
                  '&:hover': { background: '#dc2626' },
                }}
              >
                <CloseOutlined sx={{ fontSize: 11 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Drag overlay */}
        {dragging && (
          <Box sx={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: alpha(PRIMARY, 0.08),
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
          }}>
            <CloudUploadOutlined sx={{ fontSize: 14, color: PRIMARY }} />
            <Typography sx={{ fontSize: '0.68rem', color: PRIMARY, fontWeight: 700 }}>
              Drop to upload
            </Typography>
          </Box>
        )}
      </Box>

      {uploadError && (
        <Typography sx={{ fontSize: '0.63rem', color: '#dc2626', mt: 0.4 }}>
          {uploadError}
        </Typography>
      )}

      <ContentBrowserModal
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        onSelect={(url) => { onChange(url); setImgError(false); }}
        mediaType={mediaType}
        currentUrl={value || ''}
      />
    </>
  );
}